import { useState, useEffect, useCallback } from "react";
import { queueApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Plus, RefreshCw, Pencil, Trash2, Mail } from "lucide-react";
import { BoardColumns } from "../components/shelf/BoardColumns";
import { LoadPanel } from "../components/shelf/LoadPanel";
import { Leaderboard } from "../components/shelf/Leaderboard";
import { ReturnFormDialog } from "../components/shelf/ReturnFormDialog";
import { StatusChip, ComplexityChip } from "../components/shelf/ShelfChips";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Los_Angeles", label: "Pacific" },
];

const Stat = ({ label, value, hint }) => (
  <div className="rounded-xl border border-border bg-card p-5" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold tabular-nums tracking-tight mt-1.5">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

export default function ShelfBoard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [board, setBoard] = useState({ by_status: {}, load: [], config: {}, total: 0 });
  const [metrics, setMetrics] = useState(null);
  const [leaders, setLeaders] = useState(null);
  const [leaderPeriod, setLeaderPeriod] = useState("month");
  const [config, setConfig] = useState({
    wip_limit_default: 3,
    shelf_visible_count: 15,
    digest_enabled: true,
    digest_hour: 7,
    digest_minute: 30,
    digest_timezone: "America/Chicago",
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testEmail, setTestEmail] = useState("");

  const load = useCallback(async (spinner = false) => {
    if (spinner) setLoading(true);
    try {
      const [b, m, l] = await Promise.all([
        queueApi.getBoard(),
        queueApi.getMetrics(),
        queueApi.getLeaderboard(leaderPeriod),
      ]);
      setBoard(b.data);
      setMetrics(m.data);
      setLeaders(l.data);
      setConfig(b.data.config);
    } catch (e) {
      toast.error("Couldn't load the board");
    } finally {
      setLoading(false);
    }
  }, [leaderPeriod]);

  useEffect(() => {
    load(true);
  }, [load]);


  const submitForm = async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await queueApi.updateItem(editing.id, payload);
        toast.success("Work updated.");
      } else {
        await queueApi.createItem(payload);
        toast.success("Added to the shelf.");
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await queueApi.deleteItem(deleteTarget.id);
      toast.success("Deleted.");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Couldn't delete that");
    } finally {
      setBusy(false);
    }
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      const res = await queueApi.saveConfig({
        wip_limit_default: parseInt(config.wip_limit_default, 10),
        shelf_visible_count: parseInt(config.shelf_visible_count, 10),
        digest_enabled: config.digest_enabled,
        digest_hour: parseInt(config.digest_hour, 10),
        digest_minute: parseInt(config.digest_minute, 10),
        digest_timezone: config.digest_timezone,
      });
      setConfig(res.data);
      toast.success("Queue settings saved.");
    } catch (e) {
      toast.error("Couldn't save settings");
    } finally {
      setBusy(false);
    }
  };

  const allWork = Object.values(board.by_status).flat();

  const digestTime = `${String(config.digest_hour).padStart(2, "0")}:${String(config.digest_minute).padStart(2, "0")}`;

  const setDigestTime = (value) => {
    const [h, m] = value.split(":");
    setConfig({ ...config, digest_hour: parseInt(h, 10) || 0, digest_minute: parseInt(m, 10) || 0 });
  };

  const runDigest = async (previewOnly) => {
    setBusy(true);
    try {
      const res = previewOnly ? await queueApi.previewDigest(testEmail) : await queueApi.sendDigestNow();
      toast.success(
        res.data.skipped
          ? "Nothing on the shelf, so no email went out."
          : previewOnly
          ? `Test copy sent to ${testEmail || "your account email"}.`
          : `Digest sent to ${res.data.sent} people.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the digest");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="board-loading">
        <Loader2 className="animate-spin text-muted-foreground" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-7 animate-fade-in" data-testid="shelf-board-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">Shelf Board</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {board.total} jobs tracked · anything added lands straight on the shelf
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => load()} className="gap-2" data-testid="board-refresh-btn">
            <RefreshCw size={14} /> Refresh
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            data-testid="add-return-btn"
          >
            <Plus size={14} /> Add Work
          </Button>
        </div>
      </div>

      <Tabs defaultValue="board">
        <TabsList data-testid="board-tabs">
          <TabsTrigger value="board" data-testid="tab-board">Board</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-work">
            All Work ({allWork.length})
          </TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="tab-metrics">Metrics</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="board" className="mt-6 space-y-6">
          <LoadPanel load={board.load} wipLimit={config.wip_limit_default} />
          <BoardColumns byStatus={board.by_status} />
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          {allWork.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center" data-testid="all-work-empty">
              Nothing on the board yet.
            </p>
          ) : (
            <div className="space-y-3" data-testid="all-work-list">
              {allWork.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4"
                  data-testid={`all-work-item-${item.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold break-words">
                        {item.client_id && (
                          <span className="font-mono text-muted-foreground mr-2">{item.client_id}</span>
                        )}
                        {item.client_name}
                      </p>
                      <StatusChip value={item.status} />
                      <ComplexityChip value={item.complexity} />
                    </div>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      {item.work_label}
                      {item.due_date ? ` · due ${item.due_date}` : ""}
                      {item.claimed_by_name ? ` · with ${item.claimed_by_name}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Put up by {item.created_by}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(item);
                        setFormOpen(true);
                      }}
                      data-testid={`edit-btn-${item.id}`}
                    >
                      <Pencil size={13} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={() => setDeleteTarget(item)}
                      data-testid={`delete-btn-${item.id}`}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Shelf leaderboard</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Who's pulling work off the shelf. Partners only — staff never see this.
                  </p>
                </div>
                <div className="flex gap-1">
                  {[
                    { value: "week", label: "This week" },
                    { value: "month", label: "This month" },
                    { value: "all", label: "All time" },
                  ].map((p) => (
                    <Button
                      key={p.value}
                      size="sm"
                      variant={leaderPeriod === p.value ? "default" : "ghost"}
                      onClick={() => setLeaderPeriod(p.value)}
                      data-testid={`leaderboard-period-${p.value}`}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>{leaders && <Leaderboard rows={leaders.rows} />}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="mt-6 space-y-6">
          {metrics && (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Avg days on shelf" value={metrics.avg_days_on_shelf} hint="Added to claimed" />
                <Stat label="Avg claim to finish" value={metrics.avg_days_claim_to_complete} hint="In days" />
                <Stat label="Handed back" value={metrics.release_count} hint="Work returned to the shelf" />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Shelf wait by complexity</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5 text-sm">
                    {Object.keys(metrics.avg_days_on_shelf_by_complexity).length === 0 && (
                      <p className="text-xs text-muted-foreground">No claims yet</p>
                    )}
                    {Object.entries(metrics.avg_days_on_shelf_by_complexity).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="capitalize text-muted-foreground">{k}</span>
                        <span className="tabular-nums font-semibold">{v}d</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Hand-back reasons</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5 text-sm">
                    {Object.keys(metrics.release_reasons).length === 0 && (
                      <p className="text-xs text-muted-foreground">None yet</p>
                    )}
                    {Object.entries(metrics.release_reasons).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
                        <span className="tabular-nums font-semibold">{v}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Finished this week</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5 text-sm">
                    {Object.keys(metrics.completions_this_week).length === 0 && (
                      <p className="text-xs text-muted-foreground">None yet</p>
                    )}
                    {Object.entries(metrics.completions_this_week).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="tabular-nums font-semibold">{v}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle className="text-base">Queue settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  WIP limit per person
                </Label>
                <Input
                  type="number"
                  value={config.wip_limit_default}
                  onChange={(e) => setConfig({ ...config, wip_limit_default: e.target.value })}
                  data-testid="config-wip-limit-input"
                />
                <p className="text-xs text-muted-foreground">
                  Stops one person claiming everything on day one.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Jobs visible on the shelf
                </Label>
                <Input
                  type="number"
                  value={config.shelf_visible_count}
                  onChange={(e) => setConfig({ ...config, shelf_visible_count: e.target.value })}
                  data-testid="config-shelf-count-input"
                />
                <p className="text-xs text-muted-foreground">
                  The cap is the point — it stops cherry-picking the easy jobs.
                </p>
              </div>
              <Button onClick={saveConfig} disabled={busy} data-testid="config-save-btn">
                {busy ? "Saving…" : "Save settings"}
              </Button>
            </CardContent>
          </Card>

          <Card className="max-w-xl mt-6">
            <CardHeader>
              <CardTitle className="text-base">Daily shelf email</CardTitle>
              <p className="text-xs text-muted-foreground">
                One email to all staff each morning listing everything unclaimed, most urgent first.
                Nothing goes out on a day the shelf is empty.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                <Label className="text-sm font-normal">Send the daily email</Label>
                <Switch
                  checked={!!config.digest_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, digest_enabled: v })}
                  data-testid="config-digest-enabled-switch"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Send at</Label>
                  <Input
                    type="time"
                    value={digestTime}
                    onChange={(e) => setDigestTime(e.target.value)}
                    data-testid="config-digest-time-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Time zone</Label>
                  <Select
                    value={config.digest_timezone}
                    onValueChange={(v) => setConfig({ ...config, digest_timezone: v })}
                  >
                    <SelectTrigger data-testid="config-digest-timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Send a test copy to
                </Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="brandon@steelemartin.com"
                  data-testid="digest-test-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to send to the address on your own account.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveConfig} disabled={busy} data-testid="digest-save-btn">
                  {busy ? "Saving…" : "Save email settings"}
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={busy}
                  onClick={() => runDigest(true)}
                  data-testid="digest-preview-btn"
                >
                  <Mail size={14} /> Send test copy
                </Button>
                <Button
                  variant="ghost"
                  className="text-muted-foreground"
                  disabled={busy}
                  onClick={() => runDigest(false)}
                  data-testid="digest-send-now-btn"
                >
                  Send to everyone now
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ReturnFormDialog
        currentUserName={user?.name}
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) setEditing(null);
        }}
        onSubmit={submitForm}
        busy={busy}
        editing={editing}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-return-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.client_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the job and its history from the queue for good.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
